import React, { useState } from 'react';
import {
  BuildingOffice2Icon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../utils/api';
import faviconLogo from '../../favicon.png';

const STEPS = [
  { id: 1, title: 'Company Info', description: 'Tell us about your firm' },
  { id: 2, title: 'Contact Details', description: 'How clients reach you' },
  { id: 3, title: 'Preferences', description: 'Customize your workspace' },
];

const PRACTICE_AREAS = [
  'General Practice',
  'Corporate Law',
  'Criminal Law',
  'Family Law',
  'Labour Law',
  'Property Law',
  'Tax Law',
  'Immigration Law',
  'Commercial Law',
  'Constitutional Law',
  'Environmental Law',
  'Intellectual Property',
  'Mining Law',
  'Banking & Finance',
  'Insurance Law',
  'Other',
];

const FIRM_SIZES = [
  { value: 'solo', label: 'Solo Practitioner', description: '1 person' },
  { value: 'small', label: 'Small Firm', description: '2–10 people' },
  { value: 'medium', label: 'Medium Firm', description: '11–50 people' },
  { value: 'large', label: 'Large Firm', description: '51–200 people' },
  { value: 'enterprise', label: 'Enterprise', description: '200+ people' },
];

const JURISDICTIONS = [
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'BW', label: 'Botswana' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'MW', label: 'Malawi' },
  { value: 'NA', label: 'Namibia' },
  { value: 'OTHER', label: 'Other' },
];

const TIMEZONES = [
  { value: 'Africa/Harare', label: 'Africa/Harare (CAT)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
  { value: 'Africa/Lusaka', label: 'Africa/Lusaka (CAT)' },
  { value: 'Africa/Maputo', label: 'Africa/Maputo (CAT)' },
  { value: 'UTC', label: 'UTC' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'ZWL', label: 'ZWL - Zimbabwe Dollar' },
  { value: 'ZAR', label: 'ZAR - South African Rand' },
  { value: 'BWP', label: 'BWP - Botswana Pula' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'EUR', label: 'EUR - Euro' },
];

const OrganizationOnboardingModal = ({ user, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Company Info
    name: '',
    size: 'solo',
    industry: '',
    tax_id: '',
    // Step 2: Contact
    email: user?.email || '',
    phone: '',
    website: '',
    address: '',
    // Step 3: Preferences
    timezone: 'Africa/Harare',
    default_currency: 'USD',
    default_jurisdiction: 'ZW',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSizeSelect = (size) => {
    setFormData(prev => ({ ...prev, size }));
  };

  const validateStep = () => {
    setError('');
    if (currentStep === 1) {
      if (!formData.name.trim()) {
        setError('Company / firm name is required');
        return false;
      }
      if (!formData.industry) {
        setError('Please select your primary practice area');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      // If user doesn't have an organization, create one via registration update
      if (!user?.organization) {
        // Create org + onboarding in one call
        const createResponse = await api.post('/api/accounts/register-organization/', {
          name: formData.name,
          size: formData.size,
          industry: formData.industry,
          tax_id: formData.tax_id,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          address: formData.address,
          timezone: formData.timezone,
          default_currency: formData.default_currency,
          default_jurisdiction: formData.default_jurisdiction,
        });
        onComplete(createResponse.data);
      } else {
        // Organization exists, complete onboarding
        await api.post('/api/accounts/organization/onboarding/', {
          size: formData.size,
          industry: formData.industry,
          timezone: formData.timezone,
          default_currency: formData.default_currency,
          default_jurisdiction: formData.default_jurisdiction,
        });

        // Update org details
        await api.patch('/api/accounts/organization/', {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          address: formData.address,
          tax_id: formData.tax_id,
        });

        onComplete();
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.name?.[0] ||
        (typeof err.response?.data === 'object'
          ? Object.values(err.response.data).flat().join('. ')
          : 'Failed to complete setup. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-2xl bg-white dark:bg-gray-800 shadow-2xl transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-2xl px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 rounded-lg p-2">
                  <img src={faviconLogo} alt="EasyLaw" className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Set Up Your Legal Practice
                  </h2>
                  <p className="text-primary-100 text-sm mt-0.5">
                    Welcome, {user?.first_name || 'there'}! Let's get your firm configured.
                  </p>
                </div>
              </div>
              {onSkip && (
                <button
                  onClick={onSkip}
                  className="text-white/70 hover:text-white transition-colors"
                  title="Skip for now"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              )}
            </div>

            {/* Step indicator */}
            <div className="flex items-center mt-6 space-x-2">
              {STEPS.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all ${
                        currentStep > step.id
                          ? 'bg-green-400 text-white'
                          : currentStep === step.id
                          ? 'bg-white text-primary-600'
                          : 'bg-white/20 text-white/70'
                      }`}
                    >
                      {currentStep > step.id ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        step.id
                      )}
                    </div>
                    <span
                      className={`ml-2 text-sm hidden sm:inline ${
                        currentStep >= step.id ? 'text-white font-medium' : 'text-white/60'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        currentStep > step.id ? 'bg-green-400' : 'bg-white/20'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-3 rounded-lg text-sm flex items-center">
                <XMarkIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Firm / Company Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="e.g. Moyo & Associates Legal Practitioners"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Primary Practice Area <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    >
                      <option value="">Select your practice area...</option>
                      {PRACTICE_AREAS.map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Firm Size
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {FIRM_SIZES.map(size => (
                      <button
                        key={size.value}
                        type="button"
                        onClick={() => handleSizeSelect(size.value)}
                        className={`relative flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          formData.size === size.value
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <UserGroupIcon className={`h-6 w-6 mb-1 ${
                          formData.size === size.value ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.size === size.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {size.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{size.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tax / Registration Number
                  </label>
                  <input
                    type="text"
                    name="tax_id"
                    value={formData.tax_id}
                    onChange={handleChange}
                    className="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="Optional – e.g. BP Number"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Contact Details */}
            {currentStep === 2 && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="info@yourfirm.co.zw"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Office Phone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="+263 4 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Website
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="https://www.yourfirm.co.zw"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Office Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 pt-2.5 flex items-start pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="123 Samora Machel Ave, Harare, Zimbabwe"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Preferences */}
            {currentStep === 3 && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Primary Jurisdiction
                  </label>
                  <select
                    name="default_jurisdiction"
                    value={formData.default_jurisdiction}
                    onChange={handleChange}
                    className="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  >
                    {JURISDICTIONS.map(j => (
                      <option key={j.value} value={j.value}>{j.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    className="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Billing Currency
                  </label>
                  <select
                    name="default_currency"
                    value={formData.default_currency}
                    onChange={handleChange}
                    className="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Summary card */}
                <div className="mt-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-primary-800 dark:text-primary-300 mb-2">
                    Setup Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-primary-700 dark:text-primary-400">
                    <span className="font-medium">Firm:</span>
                    <span>{formData.name || '—'}</span>
                    <span className="font-medium">Practice:</span>
                    <span>{formData.industry || '—'}</span>
                    <span className="font-medium">Size:</span>
                    <span>{FIRM_SIZES.find(s => s.value === formData.size)?.label || '—'}</span>
                    <span className="font-medium">Jurisdiction:</span>
                    <span>{JURISDICTIONS.find(j => j.value === formData.default_jurisdiction)?.label || '—'}</span>
                  </div>
                  <p className="mt-3 text-xs text-primary-600 dark:text-primary-400">
                    You'll be set as the <strong>Organization Admin</strong> with full management privileges. You can invite team members after setup.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-gray-50 dark:bg-gray-750 rounded-b-2xl flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <div>
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back
                </button>
              ) : onSkip ? (
                <button
                  type="button"
                  onClick={onSkip}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Skip for now
                </button>
              ) : null}
            </div>

            <div>
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Continue
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Setting up...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Complete Setup
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationOnboardingModal;
